import React from 'react';
import { styled } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Colors } from 'library';

type RIconSize = 'sm' | 'md' | 'lg';
type RIconTone = 'default' | 'muted' | 'outlined' | 'accent';

interface RIconProps {
    icon: SvgIconComponent | IconDefinition;
    size?: RIconSize;
    tone?: RIconTone;
    iconColor?: string;
    className?: string;
}

const CIRCLE_PX: Record<RIconSize, number> = { sm: 32, md: 44, lg: 72 };
const ICON_PX: Record<RIconSize, number> = { sm: 16, md: 22, lg: 32 };

const TONE_BG: Record<RIconTone, string> = {
    default: Colors.lightGrey,
    muted: Colors.faintGrey,
    outlined: Colors.white,
    accent: Colors.lightTurq,
};

const TONE_BORDER: Record<RIconTone, string | undefined> = {
    default: undefined,
    muted: `1px solid ${Colors.lightGrey}`,
    outlined: `1px solid ${Colors.lightGrey}`,
    accent: undefined,
};

const TONE_ICON_COLOR: Record<RIconTone, string> = {
    default: Colors.mediumGrey,
    muted: Colors.mediumGrey,
    outlined: Colors.mediumGrey,
    accent: Colors.turquoise,
};

function isFaIcon(icon: SvgIconComponent | IconDefinition): icon is IconDefinition {
    return typeof icon === 'object' && icon !== null && 'iconName' in icon && 'prefix' in icon;
}

export function RIcon({ icon, size = 'md', tone = 'default', iconColor, className }: RIconProps) {
    const color = iconColor ?? TONE_ICON_COLOR[tone];
    const fontSize = ICON_PX[size];

    return (
        <Wrapper $size={size} $tone={tone} className={className}>
            {isFaIcon(icon) ? (
                <FontAwesomeIcon icon={icon} style={{ fontSize, color }} />
            ) : (
                React.createElement(icon, { sx: { fontSize, color } })
            )}
        </Wrapper>
    );
}

const shouldForwardWrapperProp = (prop: string) => !['$size', '$tone'].includes(prop);

const Wrapper = styled('div', { shouldForwardProp: (p) => shouldForwardWrapperProp(p as string) })<{
    $size: RIconSize;
    $tone: RIconTone;
}>(({ $size, $tone }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: CIRCLE_PX[$size],
    height: CIRCLE_PX[$size],
    borderRadius: '50%',
    backgroundColor: TONE_BG[$tone],
    border: TONE_BORDER[$tone],
    flexShrink: 0,
}));
